import React, { Component } from 'react';
import './AnalyticsDashboard.css';
import { isSessionActive } from '../shared/auth';

function formatDuration(seconds) {
  const totalSeconds = Math.max(0, Math.round(seconds || 0));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${remainingSeconds}s`;
}

function formatDate(value, includeTime = false) {
  const options = includeTime
    ? { dateStyle: 'medium', timeStyle: 'short' }
    : { dateStyle: 'medium' };
  return new Intl.DateTimeFormat(undefined, options).format(new Date(value));
}

export class AnalyticsDashboard extends Component {
  state = {
    data: null,
    loading: true,
    error: ''
  };

  componentDidMount() {
    this.loadSummary();
  }

  loadSummary = async () => {
    const token = localStorage.getItem('token');
    if (!token || !isSessionActive()) {
      this.setState({ loading: false, error: 'Please log in to view analytics.' });
      return;
    }

    this.setState({ loading: true, error: '' });
    try {
      const response = await fetch('/api/visits/summary', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 401) {
        throw new Error('Your session has expired. Please log in again.');
      }
      if (!response.ok) {
        throw new Error('Analytics could not be loaded.');
      }

      const data = await response.json();
      this.setState({ data, loading: false });
    } catch (error) {
      this.setState({ loading: false, error: error.message });
    }
  };

  render() {
    const { data, loading, error } = this.state;

    if (loading) {
      return <div className="analytics-state">Loading analytics…</div>;
    }

    if (error) {
      return (
        <div className="analytics-state analytics-error">
          <p>{error}</p>
          <button className="btn btn-custom" onClick={this.loadSummary}>Try again</button>
        </div>
      );
    }

    return (
      <section className="analytics-page">
        <div className="analytics-heading">
          <div>
            <span className="analytics-eyebrow">Site analytics</span>
            <h1>Visits overview</h1>
            <p>Page views, dates, and time spent on each page.</p>
          </div>
          <button className="btn btn-custom analytics-refresh" onClick={this.loadSummary}>
            Refresh
          </button>
        </div>

        <div className="analytics-metrics">
          <Metric label="Total page views" value={data.totalViews} />
          <Metric label="Unique sessions" value={data.totalSessions} />
          <Metric label="Views today" value={data.viewsToday} />
          <Metric label="Avg. visit (30 days)" value={formatDuration(data.averageDurationSeconds)} />
        </div>

        <div className="analytics-grid">
          <div className="analytics-panel">
            <h2>Last 30 days</h2>
            <div className="analytics-table-wrap">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Views</th>
                    <th>Sessions</th>
                    <th>Avg. duration</th>
                  </tr>
                </thead>
                <tbody>
                  {data.history.length === 0 ? (
                    <EmptyRow columns={4} />
                  ) : data.history.map(day => (
                    <tr key={day.date}>
                      <td>{formatDate(day.date)}</td>
                      <td>{day.views}</td>
                      <td>{day.sessions}</td>
                      <td>{formatDuration(day.averageDurationSeconds)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="analytics-panel analytics-recent-panel">
            <h2>Recent page views</h2>
            <div className="analytics-table-wrap">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Page</th>
                    <th>Date and time</th>
                    <th>Duration</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent.length === 0 ? (
                    <EmptyRow columns={4} />
                  ) : data.recent.map(visit => (
                    <tr key={visit.visitId}>
                      <td><span className="analytics-path">{visit.path}</span></td>
                      <td>{formatDate(visit.startedAtUtc, true)}</td>
                      <td>{formatDuration(visit.durationSeconds)}</td>
                      <td>
                        <span className={`analytics-status ${visit.isActive ? 'is-active' : ''}`}>
                          {visit.isActive ? 'Active' : 'Ended'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    );
  }
}

function Metric({ label, value }) {
  return (
    <div className="analytics-metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function EmptyRow({ columns }) {
  return (
    <tr>
      <td className="analytics-empty" colSpan={columns}>No visits recorded yet.</td>
    </tr>
  );
}
